import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

interface AttachOfferArgs {
  offerId: string;
  projectId: string;
  startupInvoicePct: number;
  finalInvoicePct: number;
}

interface WritingCreditArgs {
  offerId: string;
  offerTitle: string;
  createdBy: string | null;
  hours: number;
  amount: number;
}

/**
 * Knytter et tilbud til et nyopprettet prosjekt (`offers.project_id`)
 * og lagrer fakturadelingens prosentsats (backward compat med tidligere
 * UI som leste startup/final-pct direkte).
 *
 * Invalideringene treffer både det enkelte tilbudet (`offer`-detail),
 * lista (`offers`) og prosjektlista — alle tre flatene viser
 * "knyttet til prosjekt"-status.
 */
export function useAttachOfferToProject() {
  const qc = useQueryClient();
  return useMutation<void, Error, AttachOfferArgs>({
    mutationFn: async ({ offerId, projectId, startupInvoicePct, finalInvoicePct }) => {
      const { error } = await supabase
        .from("offers")
        .update({
          project_id: projectId,
          startup_invoice_pct: startupInvoicePct,
          final_invoice_pct: finalInvoicePct,
        })
        .eq("id", offerId);
      if (error) {
        throw new Error(
          `Prosjekt opprettet, men tilbudet ble ikke koblet: ${error.message}`,
        );
      }
    },
    onSuccess: async (_data, { offerId, projectId }) => {
      await invalidateFor(qc, "offers", { offerId });
      await invalidateFor(qc, "projects", { projectId });
    },
  });
}

/**
 * Krediterer tilbudsskriveren med timer + beløp på det interne
 * tilbudsskriving-prosjektet. Henter `offer_writing_internal_project_id`
 * fra `system_config` og insertter både budget_line og assignment.
 *
 * Caller behandler dette som best-effort: hvis credit-flyten feiler,
 * skal hovedflyten (konvertering) IKKE rulles tilbake. Mutationen
 * eksponerer `.mutate` (fire-and-forget) snarere enn `mutateAsync`.
 */
/**
 * @deprecated VIB-789: Erstattet av `useAssignWritingCreditOnProject` som
 * tildeler timer på prosjektets Tilbudsskriving-linje (teller mot
 * faktureringsgrad). Eldre internprosjekt-credit-mekanikk bevares for
 * historisk data; ingen nye kall skal gjøres.
 */
export function useCreateOfferWritingCredit() {
  return useMutation<void, Error, WritingCreditArgs>({
    mutationFn: async ({ offerId: _offerId, offerTitle, createdBy, hours, amount }) => {
      if (!createdBy) return; // ingen tilbudsskriver registrert
      if (hours <= 0) return;

      const { data: cfgRow } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", "offer_writing_internal_project_id")
        .maybeSingle();
      const internalProjectId =
        typeof cfgRow?.value === "string"
          ? cfgRow.value
          : (cfgRow?.value as { value?: string })?.value ?? null;
      if (!internalProjectId) return;

      const { data: bl, error: blErr } = await supabase
        .from("budget_lines")
        .insert({
          project_id: internalProjectId,
          phase: "pl",
          description: `Tilbudsskriving: ${offerTitle}`,
          cost_type: "internal",
          quantity_hours: hours,
          amount,
        })
        .select("id")
        .single();
      if (blErr || !bl?.id) {
        throw new Error(blErr?.message ?? "Manglet id på opprett-credit");
      }

      await supabase.from("budget_line_assignments").insert({
        budget_line_id: bl.id,
        user_id: createdBy,
        hours,
      });
    },
  });
}
